/**
 * 同步引擎（与 iOS SyncEngine 一致）：GET /v1/sync?since=seq 增量拉取，合并进 store。
 * 触发时机：启动、每 60 秒、回到前台、实时通道 changed{seq} 且 seq 比本地新、写操作之后。
 * 同步进行中又被触发时，本轮结束后再拉一轮。
 */
import { api, ApiError } from "./api";
import type { AgendaDay, SyncResponse } from "./models";
import { connectRealtime, disconnectRealtime, ensureRealtime, onRealtime } from "./realtime";
import { applySync, dataStore, type EntityKind, type EntityOf, removeLocal, setAgenda, setSyncStatus, upsert } from "./store";

const POLL_MS = 60_000;

let running: Promise<void> | null = null;
let again = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let started = false;
const unsubs: (() => void)[] = [];
const changeListeners = new Set<() => void>();

/** 每次同步拉到新数据后回调（例如刷新当前页面用到的日程展开） */
export function onDataChanged(fn: () => void): () => void {
  changeListeners.add(fn);
  return () => changeListeners.delete(fn);
}

async function pullOnce(): Promise<void> {
  try {
    const resp = await api.get<SyncResponse>("sync", { since: dataStore.getState().seq });
    const changed = applySync(resp);
    setSyncStatus({ lastSyncAt: Date.now(), lastError: null });
    if (changed) for (const fn of changeListeners) fn();
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    setSyncStatus({ lastError: msg });
    if (!(e instanceof ApiError && e.isUnauthorized)) console.warn("[sync] 同步失败", msg);
  }
}

/** 立即同步；正在同步时，本轮结束后再拉一轮（返回的 Promise 在全部结束后完成） */
export function syncNow(): Promise<void> {
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    setSyncStatus({ syncing: true });
    try {
      do {
        again = false;
        await pullOnce();
      } while (again);
    } finally {
      running = null;
      setSyncStatus({ syncing: false });
    }
  })();
  return running;
}

/** 不等待结果的同步（写操作之后用） */
export function syncSoon(): void {
  void syncNow();
}

function onVisibility(): void {
  if (document.visibilityState === "visible") {
    syncSoon();
    ensureRealtime();
  }
}

function onOnline(): void {
  syncSoon();
  ensureRealtime();
}

/** 登录后调用：立即同步、连接实时通道、开始定时轮询 */
export function startSync(): void {
  if (started) return;
  started = true;
  syncSoon();
  connectRealtime();
  pollTimer = setInterval(() => {
    if (document.visibilityState === "visible") syncSoon();
  }, POLL_MS);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", onOnline);
  unsubs.push(
    onRealtime("changed", (m) => {
      if (m.seq > dataStore.getState().seq) syncSoon();
    }),
    onRealtime("instant_reminder", (m) => upsert("reminderLogs", m.log)),
  );
}

/** 退出登录时调用 */
export function stopSync(): void {
  started = false;
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  document.removeEventListener("visibilitychange", onVisibility);
  window.removeEventListener("online", onOnline);
  while (unsubs.length) unsubs.pop()!();
  disconnectRealtime();
}

/** 接口单次最多 62 天 */
const AGENDA_MAX_DAYS = 62;

function addDays(date: string, n: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(t.getUTCDate()).padStart(2, "0")}`;
}

/**
 * 拉取日程展开（含两端），写入 store 的 agenda 缓存；超过 62 天自动分段。
 * 失败时保留缓存并返回 false（页面照常用缓存显示）。
 */
export async function fetchAgenda(childId: string, from: string, to: string = from): Promise<boolean> {
  try {
    let start = from;
    while (start <= to) {
      const endCandidate = addDays(start, AGENDA_MAX_DAYS - 1);
      const end = endCandidate < to ? endCandidate : to;
      const r = await api.get<{ days: AgendaDay[] }>(`children/${childId}/agenda`, { from: start, to: end });
      setAgenda(childId, r.days);
      start = addDays(end, 1);
    }
    return true;
  } catch (e) {
    console.warn("[sync] 获取日程失败", e instanceof Error ? e.message : e);
    return false;
  }
}

// ---------- 写操作辅助 ----------

/** 写操作成功后：把返回的实体放进本地并触发同步 */
export function didWrite<K extends EntityKind>(kind: K, row: EntityOf<K>): EntityOf<K> {
  upsert(kind, row);
  syncSoon();
  return row;
}

/**
 * 发一个返回实体的写请求，成功后 upsert + 同步：
 *   const ev = await writeEntity("scheduleEvents", "POST", `children/${id}/events`, body)
 */
export async function writeEntity<K extends EntityKind>(kind: K, method: "POST" | "PATCH" | "PUT", path: string, body?: unknown): Promise<EntityOf<K>> {
  const row = method === "POST" ? await api.post<EntityOf<K>>(path, body) : method === "PATCH" ? await api.patch<EntityOf<K>>(path, body ?? {}) : await api.put<EntityOf<K>>(path, body ?? {});
  return didWrite(kind, row);
}

/**
 * 删除：DELETE 成功后先从本地移除，再等同步完成（服务端可能连带删除 / 修改其它行，例如删日程会删它的提醒）。
 *   await deleteEntity("scheduleEvents", id, `events/${id}`)
 */
export async function deleteEntity(kind: EntityKind, id: string, path: string): Promise<void> {
  await api.del(path);
  removeLocal(kind, id);
  await syncNow();
}
